import { ReactNode, useId } from 'react';
import type { DescriptionProps, LabelProps } from '../use_input.hook';
import type { InputComboboxOption } from './input_combobox_root.component';

interface CommonInputComboboxProps {
	id?: string;
	name?: string;
	helperText?: string;
	invalid?: boolean;
	invalidText?: string;
	required?: boolean;
	onChange?: (item: InputComboboxOption[] | null) => void;
	value?: string[];
	defaultValue?: string[];
	readOnly?: boolean;
	optional?: boolean;
	disabled?: boolean;
}

interface UseInputComboboxProps extends CommonInputComboboxProps {
	label: string;
	hideLabel?: boolean;
	description?: string;
	multiple?: boolean;
}

interface HelperTextProps {
	id: string;
	invalid: boolean;
	children: ReactNode;
}

interface InputComboboxTriggerProps {
	id?: string;
	'aria-required'?: 'true';
	'aria-invalid'?: 'true';
	'aria-label'?: string;
	'aria-describedby'?: string;
	'aria-labelledby'?: string;
	'aria-readonly'?: boolean;
	'data-placeholder'?: boolean;
	'aria-multiselectable'?: boolean;
	role: string;
	readOnly?: boolean;
	disabled?: boolean;
}

interface UseInputComboboxReturn {
	triggerProps: InputComboboxTriggerProps;
	descriptionProps: DescriptionProps;
	helpTextProps: HelperTextProps;
	labelProps: LabelProps;
}

export function useInputCombobox({
	id,
	label,
	hideLabel,
	invalid,
	invalidText,
	helperText,
	required,
	value,
	readOnly,
	description,
	optional,
	disabled,
	multiple,
}: UseInputComboboxProps): UseInputComboboxReturn {
	// Get unique ID for the input element
	let inputId = useId();

	if (id) {
		inputId = id;
	}

	// Get id for the label
	const labelId = `${inputId}-label`;

	// Get id for description component
	const descriptionId = `${inputId}-description`;

	// Get id for help text component
	const helpTextId = `${inputId}-helpText`;

	return {
		triggerProps: {
			id: inputId,
			role: 'combobox',
			disabled,
			'aria-required': required ? 'true' : undefined,
			'aria-invalid': invalid ? 'true' : undefined,
			'aria-labelledby': !hideLabel ? labelId : undefined,
			'aria-label': hideLabel ? label : undefined,
			'aria-describedby': [descriptionId, helpTextId].join(' '),
			'aria-readonly': readOnly ?? undefined,
			'data-placeholder': value === undefined ? true : undefined,
			'aria-multiselectable': multiple ? true : undefined,
		},
		labelProps: {
			optional: required ? false : optional,
			htmlFor: inputId,
			children: hideLabel ? null : label,
		},
		descriptionProps: {
			id: descriptionId,
			children: description ?? undefined,
		},
		helpTextProps: {
			id: helpTextId,
			invalid: !!(invalid && invalidText),
			children: invalid && invalidText ? invalidText : helperText,
		},
	};
}
